import { useStaticRouteTitle } from "util/routeHelpers";
import { useCallback, useEffect, useRef, useState } from "react";

import { LinkOptions, useLocation } from "@tanstack/react-router";
import { GlobalStyles } from "@mui/material";

import {
  getZendeskAuthorizationInformation,
  getZendeskWidgetAuthorizationInformation,
} from "generated/api/integration-zendesk/integration-zendesk";
import { useFeatureFlag } from "hooks/FlagHooks";
import { useIsAuthenticated } from "core/iam/AuthenticationHooks";

const supportRoutePath: LinkOptions["to"] = "/support";

export function ZendeskWidget() {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const { data: userwayEnabled, isPending: userwayFlagLoading } =
    useFeatureFlag("userway");

  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const pageTitle = useStaticRouteTitle();

  const viewingArticleRef = useRef(false);

  const zendeskLoadReady = isAuthenticated && !userwayFlagLoading;

  const initializeWidget = useCallback(() => {
    if (zendeskLoadReady) {
      const zendeskSettings = getZendeskSettingsFunction();
      if (zendeskSettings) {
        // Configure the widget and set the authentication callback that allows users to load
        // articles in the widget
        zendeskSettings("webWidget", "updateSettings", {
          webWidget: {
            offset: {
              vertical: userwayEnabled ? "50px" : "0px",
            },
            authenticate: {
              jwtFn: (callback: (jwt: string) => void) => {
                void getZendeskWidgetAuthorizationInformation().then(
                  (response) => {
                    callback(response.payload);
                  },
                );
              },
            },
          },
        });

        // The widget has a "View Original Article" button, as well as view related article buttons.
        // However, the button links directly to the article URL, and does not automatically provide
        // authentication. As a result, clicking that button results in an authentication error.
        // To fix this, we need to intercept that button press and provide an authenticated
        // redirect to the article. We do that by directing the user to the authenticated redirect
        // provided by the support page see (src/pages/support/Support.tsx)
        zendeskSettings(
          "webWidget:on",
          "userEvent",
          (event?: { action?: string; properties?: { id: string } }) => {
            if (event?.action === "Help Center Article Viewed") {
              const interceptButtonPress = () => {
                viewingArticleRef.current = true;
                // Select the widget iframe
                const iframe = document.querySelector(
                  'iframe[id="webWidget"]',
                ) as HTMLIFrameElement | undefined;
                // Select the button within the widget
                const viewOriginalArticleButton =
                  iframe?.contentWindow?.document.body.querySelector(
                    'button[aria-label="View original article"]',
                  );

                /*
                 * Intercepts the click event for a Zendesk link and redirects the user to the
                 *  authenticated redirect.
                 */
                const onZendeskLinkClicked = async (
                  event: Event,
                  articleUrl: string,
                  isFullUrl?: boolean,
                ) => {
                  event.preventDefault();
                  event.stopImmediatePropagation();
                  await getZendeskAuthorizationInformation().then(
                    (authInfo) => {
                      const diversipediaUrl = isFullUrl
                        ? articleUrl
                        : `${authInfo.articlePath}${articleUrl}`;

                      const win = window.open(
                        `${supportRoutePath}?diversipediaUrl=${diversipediaUrl}`,
                        "_blank",
                      );
                      win?.focus();
                    },
                  );
                };

                // Add event listener to intercept Zendesk links and trigger the redirect.
                // We add the event listener to the widget iframe's body instead of the individual
                // anchor elements because Zendesk rerenders the content sometimes,
                // wiping out the event listeners and causing the intercept to fail, especially
                // on first load.
                iframe?.contentWindow?.document.body.addEventListener(
                  "click",
                  async (clickEvent) => {
                    const target = clickEvent.target as HTMLElement;
                    // Find the anchor tag within the clicked element
                    const anchorTag = target.closest(
                      'a[href*="support.communitycliq.com"]',
                    );
                    // Find the back button within the clicked element
                    const backButton = target.closest(
                      'button[aria-label="Back"]',
                    );

                    // If the back button exists, that means it was clicked, and the user is
                    // navigating out of an article.
                    if (backButton) {
                      viewingArticleRef.current = false;
                    }

                    // We only want to intercept the link press if anchorTag exists (meaning
                    // the user clicked on a Zendesk link), and viewingArticle is true (meaning
                    // the user is in an article and clicking on a related article link, and not
                    // on the Zendesk widget home page with the suggestions and search).
                    if (anchorTag && viewingArticleRef.current) {
                      await onZendeskLinkClicked(
                        clickEvent,
                        anchorTag.getAttribute("href") as string,
                        true,
                      );
                    }
                  },
                );

                if (viewOriginalArticleButton) {
                  viewOriginalArticleButton.addEventListener(
                    "click",
                    async (clickEvent) => {
                      await onZendeskLinkClicked(
                        clickEvent,
                        event.properties?.id as string,
                      );
                    },
                  );
                }
              };
              // Invoke the interceptor after all current scripts settle
              setTimeout(interceptButtonPress);
            }
          },
        );
      }
    }
  }, [zendeskLoadReady]);

  // Add the script tag to the page if zendesk is enabled
  useEffect(() => {
    if (!("zE" in window) && zendeskLoadReady) {
      const script = document.createElement("script");
      script.id = "ze-snippet";
      script.src = `https://static.zdassets.com/ekr/snippet.js?key=bcd2e7ac-7e4a-4cc1-a203-9054497f72b8`;
      script.addEventListener("load", () => {
        setScriptLoaded(true);
        initializeWidget();
      });

      document.body.appendChild(script);
    }
  }, [zendeskLoadReady]);

  // Change the location of the widget when the path changes
  useEffect(() => {
    const zendeskSettings = getZendeskSettingsFunction();
    zendeskSettings?.("webWidget", "helpCenter:setSuggestions", {
      url: true,
      search: pageTitle,
    });
  }, [location, scriptLoaded]);

  // Reauthenticate/logout from the widget when the user logs in/out or zendesk widget is disabled.
  useEffect(() => {
    const zendeskSettings = getZendeskSettingsFunction();
    if (isAuthenticated) {
      zendeskSettings?.("webWidget", "helpCenter:reauthenticate");
      zendeskSettings?.("webWidget", "show");
      zendeskSettings?.("webWidget", "updateSettings", {
        webWidget: {
          zIndex: 1000,
        },
      });
    } else {
      zendeskSettings?.("webWidget", "logout");
      zendeskSettings?.("webWidget", "hide");
    }
  }, [isAuthenticated, scriptLoaded]);

  // Zendesk keeps the widget at its fixed 374px desktop width regardless of viewport, so at
  // narrow widths (e.g. 320px reflow) it overflows off the left edge of the screen.
  // Fails reflow accessibility issue.
  return (
    <GlobalStyles
      styles={{
        "iframe#webWidget": {
          maxWidth: "100% !important",
        },
      }}
    />
  );
}

function getZendeskSettingsFunction() {
  return "zE" in window
    ? (window.zE as
        | ((action: string, eventOrProp: string, parameters?: unknown) => void)
        | undefined)
    : undefined;
}
